import {useCallback, useMemo} from "react";
import { useAppDispatch, useAppSelector } from "../../app/store/hooks.ts";
import {addGeneration, replaceOptimisticGeneration, updateBalance} from "../../app/store/slices/userSlice.ts";
import {clearInitialPhoto, setSelectedPreset} from "../../app/store/slices/generationSlice.ts";
import { GenerationApi } from "../api/generationApi.ts";
import { EffectsEnum } from "../../shared/types/EffectsEnum.ts";
import { GenerationStatusEnum } from "../../shared/types/GenerationStatusEnum.ts";
import type { TGeneration } from "../../shared/types/GenerationType.ts";
import {InputEnum} from "../../shared/types/EffectType.ts";
import {setPopUpOpen} from "../../app/store/slices/appSlice.ts";

export const useStartGeneration = () => {
    const dispatch = useAppDispatch();
    const generationService = useMemo(() => new GenerationApi(), []);
    const initialPhoto = useAppSelector((state) => state.generation.prompts.find((prompt) => prompt.type === InputEnum.InitialImage)?.value);
    const textPrompt = useAppSelector((state) => state.generation.prompts.find((prompt) => prompt.type === InputEnum.TextPrompt)?.value);
    const effects = useAppSelector((state) => state.presets.videoEffects);
    const userBalance = useAppSelector((state) => state.user.user?.balance ?? 0);

    const getPriceByEffectId = (effectId: EffectsEnum) => {
        const effect = effects.find((effect) => effect.id === effectId);
        if (!effect) {
            console.error('Effect id not find in effects list for getPriceByEffectId')
            return 0
        }
        return effect.price
    }

    const startGeneration = useCallback(async (effectId: EffectsEnum) => {
        const price = getPriceByEffectId(effectId)
        const optimicticUserBalance = userBalance - price
        if (!initialPhoto || !textPrompt) return;
        const generationBody = {
            initialPhoto,
            textPrompt,
            effectId,
        }

        const optimisticId = Date.now();
        const optimisticGeneration: TGeneration = {
            id: optimisticId,
            status: GenerationStatusEnum.PROCESSING,
            isOptimistic: true,
        };



        dispatch(updateBalance(optimicticUserBalance))
        dispatch(addGeneration(optimisticGeneration));
        dispatch(setSelectedPreset(null));
        dispatch(clearInitialPhoto())
        dispatch(setPopUpOpen({isOpen: true, message: 'Generation started' }))

        try {
            const generation = await generationService.startGeneration(generationBody.initialPhoto, generationBody.effectId, generationBody.textPrompt);
            dispatch(replaceOptimisticGeneration({ optimisticId, generation }));
        } catch (error) {
            console.error('Failed to start generation:', error);
            const failedGeneration: TGeneration = {
                ...optimisticGeneration,
                status: GenerationStatusEnum.FAILED,
                error: error instanceof Error ? error.message : 'Failed to start generation',
                isOptimistic: false,
            };

            dispatch(replaceOptimisticGeneration({ optimisticId, generation: failedGeneration }));
        }
    }, [dispatch, generationService, initialPhoto, textPrompt, userBalance]);

    return { startGeneration };
};
